import React, { useState, useEffect } from 'react';
import { FaEnvelope, FaEdit, FaCheck, FaTimes, FaFileMedical, FaHistory, FaPrint, FaDownload, FaBell, FaCalendarAlt } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';

function getAllAppointments(email) {
  const all = JSON.parse(localStorage.getItem('appointments') || '[]');
  return all.filter(a => a.email === email).sort((a, b) => {
    const dateA = new Date(`${a.date}T${a.time}`);
    const dateB = new Date(`${b.date}T${b.time}`);
    return dateA - dateB;
  });
}

function getMedicalRecords(email) {
  const records = JSON.parse(localStorage.getItem('medicalRecords') || '[]');
  return records.filter(r => r.email === email);
}

function getUpcomingAppointment(email) {
  const now = new Date();
  return getAllAppointments(email)
    .find(a => {
      const appDate = new Date(`${a.date}T${a.time}`);
      return appDate > now && a.status !== 'cancelled';
    });
}

function getCustomerProfile(email) {
  const users = JSON.parse(localStorage.getItem('users') || '[]');
  const reqs = JSON.parse(localStorage.getItem('registrationRequests') || '[]');
  return users.find(u => u.email === email) || reqs.find(r => r.email === email) || { email };
}

function updateAppointment(id, newDate, newTime) {
  const all = JSON.parse(localStorage.getItem('appointments') || '[]');
  const idx = all.findIndex(a => a.id === id);
  if (idx !== -1) {
    all[idx].date = newDate;
    all[idx].time = newTime;
    all[idx].status = 'rescheduled';
    localStorage.setItem('appointments', JSON.stringify(all));
    return true;
  }
  return false;
}

const CustomerPortal = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [entered, setEntered] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [profile, setProfile] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [therapistFilter, setTherapistFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editId, setEditId] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [msg, setMsg] = useState('');
  const [activeTab, setActiveTab] = useState('appointments');
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [upcomingAppointment, setUpcomingAppointment] = useState(null);
  const [showReminder, setShowReminder] = useState(false);

  useEffect(() => {
    if (entered && email) {
      const records = getMedicalRecords(email.trim());
      setMedicalRecords(records);
      const upcoming = getUpcomingAppointment(email.trim());
      setUpcomingAppointment(upcoming);
      if (upcoming) {
        const appDate = new Date(`${upcoming.date}T${upcoming.time}`);
        const now = new Date();
        const hoursUntil = Math.round((appDate - now) / (1000 * 60 * 60));
        setShowReminder(hoursUntil <= 24 && hoursUntil > 0);
      }
    }
  }, [entered, email]);

  const handleEmailSubmit = e => {
    e.preventDefault();
    const appts = getAllAppointments(email.trim());
    setAppointments(appts);
    setProfile(getCustomerProfile(email.trim()));
    setEntered(true);
    setMsg(appts.length === 0 ? 'No appointments found.' : '');
  };

  const handleReschedule = (id) => {
    setEditId(id);
    const appt = appointments.find(a => a.id === id);
    setNewDate(appt.date);
    setNewTime(appt.time);
  };

  const handleSave = (id) => {
    if (!newDate || !newTime) {
      setMsg('Please select a new date and time.');
      return;
    }
    if (updateAppointment(id, newDate, newTime)) {
      setMsg('Appointment rescheduled!');
      const appts = getAllAppointments(email.trim());
      setAppointments(appts);
      setEditId(null);
    } else {
      setMsg('Failed to reschedule.');
    }
  };

  // Filtering logic
  const uniqueTherapists = Array.from(new Set(appointments.map(a => a.therapist))).filter(Boolean);
  const filtered = appointments.filter(appt => {
    if (statusFilter !== 'all' && appt.status !== statusFilter) return false;
    if (therapistFilter && appt.therapist !== therapistFilter) return false;
    if (dateFrom && appt.date < dateFrom) return false;
    if (dateTo && appt.date > dateTo) return false;
    return true;
  });

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    const data = {
      profile,
      appointments: filtered,
      medicalRecords,
      exportDate: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `medical_records_${email}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto my-6 sm:my-12 bg-white p-4 sm:p-6 md:p-10 rounded-xl shadow-lg">
      <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-purple-700 text-center">HealthBridge Patient Portal</h2>
      {showReminder && upcomingAppointment && (
        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start sm:items-center gap-2 sm:gap-3">
          <FaBell className="text-yellow-500 text-lg sm:text-xl flex-shrink-0 mt-1 sm:mt-0" />
          <div>
            <div className="font-semibold text-yellow-800 text-sm sm:text-base">Upcoming Appointment Reminder</div>
            <div className="text-xs sm:text-sm text-yellow-700">
              You have an appointment for {upcomingAppointment.service} with {upcomingAppointment.therapist} 
              on {upcomingAppointment.date} at {upcomingAppointment.time}
            </div>
          </div>
        </div>
      )}
      {!entered ? (
        <form onSubmit={handleEmailSubmit} className="flex flex-col items-center gap-3 sm:gap-4 mb-4">
          <div className="flex items-center gap-2 w-full max-w-md">
            <FaEnvelope className="text-purple-500" />
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="border px-3 sm:px-4 py-2 rounded focus:ring-2 focus:ring-purple-400 w-full"
              required
            />
          </div>
          <button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white px-4 sm:px-6 py-2 rounded font-semibold w-full max-w-md">Show My Appointments</button>
        </form>
      ) : (
        <>
          {/* Quick Actions */}
          <div className="mb-4 sm:mb-6 flex flex-wrap gap-2 sm:gap-4 justify-center sm:justify-start">
            <button
              onClick={() => setActiveTab('appointments')}
              className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm ${activeTab === 'appointments' ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-700'}`}
            >
              <FaCalendarAlt /> {t('appointments')}
            </button>
            <button
              onClick={() => setActiveTab('records')}
              className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm ${activeTab === 'records' ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-700'}`}
            >
              <FaFileMedical /> {t('medicalRecords')}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm ${activeTab === 'history' ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-700'}`}
            >
              <FaHistory /> {t('visitHistory')}
            </button>
            <button onClick={handlePrint} className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm bg-blue-100 text-blue-700">
              <FaPrint /> {t('print')}
            </button>
            <button onClick={handleExport} className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm bg-green-100 text-green-700">
              <FaDownload /> {t('exportData')}
            </button>
          </div>

          {/* Profile Info */}
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-4 sm:gap-8 text-xs sm:text-sm">
              <div>
                <div className="font-semibold">{t('name')}:</div>
                <div className="truncate max-w-[150px] sm:max-w-full">{profile?.name || '—'}</div>
              </div>
              <div>
                <div className="font-semibold">{t('email')}:</div>
                <div className="truncate max-w-[150px] sm:max-w-full">{profile?.email}</div>
              </div>
              <div>
                <div className="font-semibold">{t('registered')}:</div>
                <div>{profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString() : (profile?.id ? new Date(profile.id).toLocaleDateString() : '—')}</div>
              </div>
              <div>
                <div className="font-semibold">{t('totalAppointments')}:</div>
                <div>{appointments.length}</div>
              </div>
            </div>
          </div>

          {msg && <div className="mb-4 text-center text-red-600">{msg}</div>}
          <div>
            <button onClick={() => { setEntered(false); setAppointments([]); setMsg(''); setProfile(null); }} className="mb-4 text-purple-600 underline">Change Email</button>
          </div>
          
          {activeTab === 'appointments' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-4 items-stretch sm:items-end">
                <div className="w-full sm:w-auto">
                  <label className="block text-xs font-semibold mb-1">{t('status')}</label>
                  <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border px-2 py-1 rounded w-full sm:w-32">
                    <option value="all">{t('all')}</option>
                    <option value="pending">{t('pending')}</option>
                    <option value="confirmed">{t('confirmed')}</option>
                    <option value="cancelled">{t('cancelled')}</option>
                    <option value="rescheduled">{t('rescheduled')}</option>
                    <option value="no-show">{t('noShow')}</option>
                    <option value="completed">{t('completed')}</option>
                    <option value="in-progress">{t('inProgress')}</option>
                    <option value="awaiting-payment">{t('awaitingPayment')}</option>
                  </select>
                </div>
                <div className="w-full sm:w-auto">
                  <label className="block text-xs font-semibold mb-1">{t('doctor')}</label>
                  <select value={therapistFilter} onChange={e => setTherapistFilter(e.target.value)} className="border px-2 py-1 rounded w-full sm:w-40">
                    <option value="">{t('all')}</option>
                    {uniqueTherapists.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="w-full sm:w-auto">
                  <label className="block text-xs font-semibold mb-1">{t('dateFrom')}</label>
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="border px-2 py-1 rounded w-full sm:w-40" />
                </div>
                <div className="w-full sm:w-auto">
                  <label className="block text-xs font-semibold mb-1">{t('dateTo')}</label>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="border px-2 py-1 rounded w-full sm:w-40" />
                </div>
                <button
                  onClick={() => { setStatusFilter('all'); setTherapistFilter(''); setDateFrom(''); setDateTo(''); }}
                  className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-2 rounded text-xs font-semibold mt-2 sm:mt-0"
                >{t('clearFilters')}</button>
              </div>
            </div>
          )}

          {msg && <div className="mb-4 text-center text-red-600">{msg}</div>}
          <div>
            <button onClick={() => { setEntered(false); setAppointments([]); setMsg(''); setProfile(null); }} className="mb-4 text-purple-600 underline">Change Email</button>
          </div>
          
          {activeTab === 'records' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-purple-700">Medical Records</h3>
              {medicalRecords.length === 0 ? (
                <p className="text-gray-500">No medical records available.</p>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {medicalRecords.map((record, index) => (
                    <div key={index} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                      <div className="font-semibold mb-2">{record.type}</div>
                      <div className="text-sm text-gray-600">
                        <div>Date: {record.date}</div>
                        <div>Doctor: {record.doctor}</div>
                        <div>Diagnosis: {record.diagnosis}</div>
                        {record.prescription && (
                          <div className="mt-2 p-2 bg-blue-50 rounded">
                            <div className="font-semibold text-blue-700">Prescription</div>
                            <div className="whitespace-pre-line">{record.prescription}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-purple-700">Visit History</h3>
              <div className="grid gap-4">
                {appointments.filter(a => a.status === 'completed').map((visit, index) => (
                  <div key={index} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-semibold">{visit.service}</div>
                        <div className="text-sm text-gray-600">
                          <div>Date: {visit.date} at {visit.time}</div>
                          <div>Doctor: {visit.doctor}</div>
                          {visit.followUp && (
                            <div className="mt-2 text-purple-600">
                              Follow-up recommended in {visit.followUp} days
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        Reference: #{visit.id}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'appointments' && (
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden mb-6 text-xs">
                <thead>
                  <tr className="bg-purple-50">
                    <th className="py-2 px-2 sm:px-3 border">{t('date')}</th>
                    <th className="py-2 px-2 sm:px-3 border">{t('time')}</th>
                    <th className="py-2 px-2 sm:px-3 border hidden sm:table-cell">{t('service')}</th>
                    <th className="py-2 px-2 sm:px-3 border hidden sm:table-cell">{t('therapist')}</th>
                    <th className="py-2 px-2 sm:px-3 border">{t('status')}</th>
                    <th className="py-2 px-2 sm:px-3 border hidden md:table-cell">{t('notes')}</th>
                    <th className="py-2 px-2 sm:px-3 border">{t('action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(appt => (
                    <tr key={appt.id} className="hover:bg-purple-50">
                      <td className="py-2 px-2 sm:px-3 border text-xs">
                        {editId === appt.id ? (
                          <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="border px-1 sm:px-2 py-1 rounded w-full" />
                        ) : appt.date}
                      </td>
                      <td className="py-2 px-2 sm:px-3 border text-xs">
                        {editId === appt.id ? (
                          <input type="time" value={newTime} onChange={e => setNewTime(e.target.value)} className="border px-1 sm:px-2 py-1 rounded w-full" />
                        ) : appt.time}
                      </td>
                      <td className="py-2 px-2 sm:px-3 border hidden sm:table-cell">{appt.service}</td>
                      <td className="py-2 px-2 sm:px-3 border hidden sm:table-cell">{appt.therapist}</td>
                      <td className="py-2 px-2 sm:px-3 border capitalize">{appt.status}</td>
                      <td className="py-2 px-2 sm:px-3 border max-w-xs whitespace-pre-line break-words hidden md:table-cell">{appt.notes}</td>
                      <td className="py-2 px-2 sm:px-3 border text-center">
                        {editId === appt.id ? (
                          <div className="flex justify-center gap-1 sm:gap-2">
                            <button onClick={() => handleSave(appt.id)} className="text-green-600"><FaCheck /></button>
                            <button onClick={() => setEditId(null)} className="text-red-600"><FaTimes /></button>
                          </div>
                        ) : (
                          <button onClick={() => handleReschedule(appt.id)} className="text-blue-600 text-xs whitespace-nowrap"><FaEdit /> <span className="hidden sm:inline">{t('reschedule')}</span></button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-4 text-gray-500">No appointments match your filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
  };

export default CustomerPortal;
